import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  ArrowRight,
  Calendar,
  ClipboardList,
  Columns3,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import SEO from "../Components/SEO";
import TheNav from "../Components/navbar";
import EntitlementGate from "../Components/shared/EntitlementGate";
import { useAuth } from "../Context/AuthContext";
import { db } from "../Context/firebaseConfig";
import useEntitlements from "../hooks/useEntitlements";
import {
  CREATOR_PROJECT_STATUSES,
  buildCreatorProjectPayload,
  buildInstructionTemplatePayload,
  isCreatorProjectValid,
  isInstructionTemplateValid,
} from "../utils/creatorTools";

const MINT = "#10b981";
const PURPLE = "#a855f7";

const EMPTY_TEMPLATE_FORM = {
  name: "",
  instructions: "",
};

const EMPTY_PROJECT_FORM = {
  title: "",
  status: "Idea",
  notes: "",
  editorName: "",
  editorId: "",
  dueDate: "",
};

function SectionShell({ eyebrow, title, description, icon: Icon, children }) {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40">
            <Sparkles size={13} style={{ color: MINT }} />
            {eyebrow}
          </div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-white">{title}</h2>
          {description && (
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/55">
              {description}
            </p>
          )}
        </div>
        {Icon && (
          <div
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border"
            style={{
              background: `${PURPLE}18`,
              borderColor: `${PURPLE}55`,
              color: PURPLE,
            }}
          >
            <Icon size={20} />
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="min-h-[44px] w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-sm text-white outline-none transition placeholder:text-white/25 focus:border-emerald-300/35"
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, placeholder, rows = 4 }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
        {label}
      </span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-relaxed text-white outline-none transition placeholder:text-white/25 focus:border-emerald-300/35"
      />
    </label>
  );
}

function StatusSelect({ value, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/45">
        Status
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[44px] w-full rounded-2xl border border-white/10 bg-[#12101a] px-4 text-sm text-white outline-none transition focus:border-emerald-300/35"
      >
        {CREATOR_PROJECT_STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
    </label>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 text-sm leading-relaxed text-white/45">
      {children}
    </div>
  );
}

function TemplateForm({
  form,
  onChange,
  onSubmit,
  editingId,
  onCancel,
  saving,
}) {
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="grid gap-3">
        <Field
          label="Template name"
          value={form.name}
          onChange={(value) => onChange({ ...form, name: value })}
          placeholder="Short-form gaming edit"
        />
        <TextAreaField
          label="Instructions"
          value={form.instructions}
          onChange={(value) => onChange({ ...form, instructions: value })}
          placeholder="Pacing, captions, music, delivery notes..."
        />
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-400 px-4 text-sm font-bold text-black transition hover:bg-emerald-300 disabled:cursor-wait disabled:opacity-70"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={16} />}
          {editingId ? "Save template" : "Create template"}
        </button>
        {editingId && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-sm font-bold text-white transition hover:bg-white/[0.08]"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function InstructionTemplates({
  allowed,
  templates,
  form,
  setForm,
  editingId,
  onEdit,
  onCancel,
  onDelete,
  onSubmit,
  saving,
}) {
  return (
    <EntitlementGate
      allowed={allowed}
      title="Reusable editing instruction templates"
      description="Creator Plus unlocks reusable editing notes for pacing, captions, music, and delivery details."
      cta="Upgrade for templates"
    >
      <SectionShell
        eyebrow="Creator Plus"
        title="Instruction Templates"
        description="Save reusable editing instructions you can copy into future orders."
        icon={FileText}
      >
        <TemplateForm
          form={form}
          onChange={setForm}
          onSubmit={onSubmit}
          editingId={editingId}
          onCancel={onCancel}
          saving={saving}
        />
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {templates.length === 0 ? (
            <EmptyState>Create your first reusable template above.</EmptyState>
          ) : (
            templates.map((template) => (
              <article
                key={template.id}
                className="rounded-2xl border border-white/10 bg-white/[0.025] p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-bold text-white">{template.name}</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(template)}
                      className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 hover:text-white"
                      aria-label={`Edit ${template.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(template.id)}
                      className="grid h-9 w-9 place-items-center rounded-full border border-red-300/20 bg-red-500/10 text-red-100 hover:bg-red-500/20"
                      aria-label={`Delete ${template.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-white/55">
                  {template.instructions}
                </p>
              </article>
            ))
          )}
        </div>
      </SectionShell>
    </EntitlementGate>
  );
}

function ProjectForm({
  form,
  onChange,
  onSubmit,
  editingId,
  onCancel,
  saving,
  title,
  submitLabel,
}) {
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <h3 className="mb-4 text-sm font-bold text-white">{title}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Project title"
          value={form.title}
          onChange={(value) => onChange({ ...form, title: value })}
          placeholder="Friday upload edit"
        />
        <StatusSelect
          value={form.status}
          onChange={(value) => onChange({ ...form, status: value })}
        />
        <Field
          label="Editor name"
          value={form.editorName}
          onChange={(value) => onChange({ ...form, editorName: value })}
          placeholder="Optional"
        />
        <Field
          label="Editor ID"
          value={form.editorId}
          onChange={(value) => onChange({ ...form, editorId: value })}
          placeholder="Optional"
        />
        <Field
          label="Due date"
          type="date"
          value={form.dueDate}
          onChange={(value) => onChange({ ...form, dueDate: value })}
        />
      </div>
      <div className="mt-3">
        <TextAreaField
          label="Notes"
          value={form.notes}
          onChange={(value) => onChange({ ...form, notes: value })}
          placeholder="Video idea, references, deadline notes..."
          rows={3}
        />
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-400 px-4 text-sm font-bold text-black transition hover:bg-emerald-300 disabled:cursor-wait disabled:opacity-70"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={16} />}
          {submitLabel}
        </button>
        {editingId && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-sm font-bold text-white transition hover:bg-white/[0.08]"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function ProjectCard({ project, onEdit, onDelete }) {
  return (
    <article className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-white">{project.title}</h3>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-white/45">
            <span className="rounded-full border border-emerald-300/20 bg-emerald-500/10 px-2.5 py-1 font-semibold text-emerald-100">
              {project.status}
            </span>
            {project.dueDate && (
              <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1">
                <Calendar size={12} />
                {project.dueDate}
              </span>
            )}
            {project.editorName && <span>{project.editorName}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onEdit(project)}
            className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 hover:text-white"
            aria-label={`Edit ${project.title}`}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(project.id)}
            className="grid h-9 w-9 place-items-center rounded-full border border-red-300/20 bg-red-500/10 text-red-100 hover:bg-red-500/20"
            aria-label={`Delete ${project.title}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {project.notes && (
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-white/55">
          {project.notes}
        </p>
      )}
    </article>
  );
}

function ProjectOrganization({
  allowed,
  projects,
  form,
  setForm,
  editingId,
  onEdit,
  onCancel,
  onDelete,
  onSubmit,
  saving,
}) {
  return (
    <EntitlementGate
      allowed={allowed}
      title="Project organization tools"
      description="Creator Plus unlocks a private project list for ideas, deadlines, editor notes, and status tracking."
      cta="Upgrade for project tools"
    >
      <SectionShell
        eyebrow="Creator Plus"
        title="Project Organization"
        description="Track your content ideas and active edits without creating marketplace orders."
        icon={ClipboardList}
      >
        <ProjectForm
          form={form}
          onChange={setForm}
          onSubmit={onSubmit}
          editingId={editingId}
          onCancel={onCancel}
          saving={saving}
          title={editingId ? "Edit project" : "Add project"}
          submitLabel={editingId ? "Save project" : "Add project"}
        />
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {projects.length === 0 ? (
            <EmptyState>Add your first project idea above.</EmptyState>
          ) : (
            projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          )}
        </div>
      </SectionShell>
    </EntitlementGate>
  );
}

function WorkflowBoard({ allowed, projects }) {
  const projectsByStatus = useMemo(
    () =>
      CREATOR_PROJECT_STATUSES.reduce((groups, status) => {
        groups[status] = projects.filter((project) => project.status === status);
        return groups;
      }, {}),
    [projects]
  );

  return (
    <EntitlementGate
      allowed={allowed}
      title="Content workflow board"
      description="Creator Studio unlocks a board view for planning content from idea through review."
      cta="Upgrade for Studio tools"
    >
      <SectionShell
        eyebrow="Creator Studio"
        title="Content Workflow Board"
        description="A simple Kanban-style view of the same projects from your organization list."
        icon={Columns3}
      >
        <div className="grid gap-3 lg:grid-cols-5">
          {CREATOR_PROJECT_STATUSES.map((status) => (
            <div
              key={status}
              className="min-h-[180px] rounded-2xl border border-white/10 bg-black/20 p-3"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold text-white">{status}</h3>
                <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-xs text-white/45">
                  {projectsByStatus[status]?.length || 0}
                </span>
              </div>
              <div className="space-y-2">
                {projectsByStatus[status]?.length ? (
                  projectsByStatus[status].map((project) => (
                    <div
                      key={project.id}
                      className="rounded-xl border border-white/10 bg-white/[0.04] p-3"
                    >
                      <p className="text-sm font-semibold text-white">{project.title}</p>
                      {project.dueDate && (
                        <p className="mt-1 text-xs text-white/40">{project.dueDate}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="rounded-xl border border-dashed border-white/10 p-3 text-xs text-white/30">
                    No projects
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </SectionShell>
    </EntitlementGate>
  );
}

function BatchPlanning({ allowed, form, setForm, onSubmit, saving }) {
  return (
    <EntitlementGate
      allowed={allowed}
      title="Batch planning"
      description="Creator Studio unlocks draft planning for multiple project ideas. This does not create real orders yet."
      cta="Upgrade for batch planning"
    >
      <SectionShell
        eyebrow="Creator Studio"
        title="Batch Planning"
        description="Draft multiple order ideas one at a time. Marketplace order submission is intentionally not connected here yet."
        icon={Plus}
      >
        <ProjectForm
          form={form}
          onChange={setForm}
          onSubmit={onSubmit}
          editingId=""
          onCancel={() => {}}
          saving={saving}
          title="Draft a batch project idea"
          submitLabel="Add draft"
        />
      </SectionShell>
    </EntitlementGate>
  );
}

function StudioReviewCta({ allowed }) {
  return (
    <SectionShell
      eyebrow="Creator Studio"
      title="Discounted Creator Review"
      description="Studio members get a discounted review path once discount logic is wired."
      icon={Sparkles}
    >
      <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
        <p className="text-sm leading-relaxed text-white/55">
          {allowed
            ? "Your Studio plan is eligible for discounted Creator Review. Checkout discount handling is coming next."
            : "Upgrade to Creator Studio to become eligible for discounted Creator Review."}
        </p>
        <Link
          to="/pricing"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/[0.08]"
        >
          View pricing
          <ArrowRight size={15} />
        </Link>
      </div>
    </SectionShell>
  );
}

export default function CreatorTools() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE_FORM);
  const [templateEditingId, setTemplateEditingId] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT_FORM);
  const [projectEditingId, setProjectEditingId] = useState("");
  const [projectSaving, setProjectSaving] = useState(false);
  const [batchForm, setBatchForm] = useState(EMPTY_PROJECT_FORM);
  const [batchSaving, setBatchSaving] = useState(false);
  const { entitlements } = useEntitlements(userData);

  const showNotice = (message, type = "success") => {
    setNotice({ message, type });
    window.setTimeout(() => setNotice(null), 3200);
  };

  const templatesCollection = currentUser
    ? collection(db, "Users", currentUser.uid, "creatorInstructionTemplates")
    : null;
  const projectsCollection = currentUser
    ? collection(db, "Users", currentUser.uid, "creatorProjects")
    : null;

  const loadCreatorTools = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userSnap = await getDoc(doc(db, "Users", currentUser.uid));
      setUserData(userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null);

      const [templateSnap, projectSnap] = await Promise.all([
        getDocs(query(templatesCollection, orderBy("updatedAt", "desc"))),
        getDocs(query(projectsCollection, orderBy("updatedAt", "desc"))),
      ]);

      setTemplates(
        templateSnap.docs.map((templateDoc) => ({
          id: templateDoc.id,
          ...templateDoc.data(),
        }))
      );
      setProjects(
        projectSnap.docs.map((projectDoc) => ({
          id: projectDoc.id,
          ...projectDoc.data(),
        }))
      );
    } catch (error) {
      console.error("Failed to load creator tools:", error);
      showNotice("Failed to load Creator Tools.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreatorTools();
  }, [currentUser]);

  const handleTemplateSubmit = async (event) => {
    event.preventDefault();
    const payload = buildInstructionTemplatePayload(templateForm);
    if (!isInstructionTemplateValid(payload)) {
      showNotice("Add a template name and instructions.", "error");
      return;
    }

    setTemplateSaving(true);
    try {
      if (templateEditingId) {
        await updateDoc(doc(db, "Users", currentUser.uid, "creatorInstructionTemplates", templateEditingId), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
        showNotice("Template updated.");
      } else {
        await addDoc(templatesCollection, {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        showNotice("Template created.");
      }
      setTemplateForm(EMPTY_TEMPLATE_FORM);
      setTemplateEditingId("");
      await loadCreatorTools();
    } catch (error) {
      console.error("Failed to save template:", error);
      showNotice("Failed to save template.", "error");
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleTemplateEdit = (template) => {
    setTemplateEditingId(template.id);
    setTemplateForm({
      name: template.name || "",
      instructions: template.instructions || "",
    });
  };

  const handleTemplateDelete = async (templateId) => {
    try {
      await deleteDoc(doc(db, "Users", currentUser.uid, "creatorInstructionTemplates", templateId));
      showNotice("Template deleted.");
      await loadCreatorTools();
    } catch (error) {
      console.error("Failed to delete template:", error);
      showNotice("Failed to delete template.", "error");
    }
  };

  const saveProject = async ({ form, editingId = "", reset, setSaving, success }) => {
    const payload = buildCreatorProjectPayload(form);
    if (!isCreatorProjectValid(payload)) {
      showNotice("Add a project title.", "error");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, "Users", currentUser.uid, "creatorProjects", editingId), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(projectsCollection, {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      showNotice(success);
      reset();
      await loadCreatorTools();
    } catch (error) {
      console.error("Failed to save project:", error);
      showNotice("Failed to save project.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleProjectSubmit = async (event) => {
    event.preventDefault();
    await saveProject({
      form: projectForm,
      editingId: projectEditingId,
      reset: () => {
        setProjectForm(EMPTY_PROJECT_FORM);
        setProjectEditingId("");
      },
      setSaving: setProjectSaving,
      success: projectEditingId ? "Project updated." : "Project added.",
    });
  };

  const handleProjectEdit = (project) => {
    setProjectEditingId(project.id);
    setProjectForm({
      title: project.title || "",
      status: project.status || "Idea",
      notes: project.notes || "",
      editorName: project.editorName || "",
      editorId: project.editorId || "",
      dueDate: project.dueDate || "",
    });
  };

  const handleProjectDelete = async (projectId) => {
    try {
      await deleteDoc(doc(db, "Users", currentUser.uid, "creatorProjects", projectId));
      showNotice("Project deleted.");
      await loadCreatorTools();
    } catch (error) {
      console.error("Failed to delete project:", error);
      showNotice("Failed to delete project.", "error");
    }
  };

  const handleBatchSubmit = async (event) => {
    event.preventDefault();
    await saveProject({
      form: { ...batchForm, status: batchForm.status || "Idea" },
      reset: () => setBatchForm(EMPTY_PROJECT_FORM),
      setSaving: setBatchSaving,
      success: "Batch draft added.",
    });
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-[#07060d] text-white">
        <SEO title="Creator Tools | Kre8" canonicalPath="/creator-tools" noindex />
        <TheNav />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center">
          <h1 className="text-3xl font-bold">Sign in to use Creator Tools</h1>
          <Link
            to="/login"
            className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-emerald-400 px-5 py-3 text-sm font-bold text-black"
          >
            Log in
            <ArrowRight size={16} />
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07060d] text-white">
      <SEO
        title="Creator Tools | Kre8"
        description="Private creator workflow tools for Kre8 creators."
        canonicalPath="/creator-tools"
        noindex
      />
      <TheNav />
      <main className="relative mx-auto max-w-7xl px-4 pb-20 pt-10 sm:px-6">
        <div
          className="pointer-events-none absolute -top-24 left-1/2 h-[360px] w-[720px] -translate-x-1/2 rounded-full opacity-20 blur-[120px]"
          style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
        />
        <div className="relative">
          <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40">
                <Sparkles size={13} style={{ color: MINT }} />
                Private creator workspace
              </div>
              <h1 className="mt-3 text-4xl font-black tracking-tight text-white sm:text-5xl">
                Creator Tools
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/55 sm:text-base">
                Plan content, save reusable instructions, and organize project ideas
                without changing marketplace orders or your public profile.
              </p>
            </div>
            <Link
              to="/creatorPortfolio"
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm font-bold text-white transition hover:bg-white/[0.08]"
            >
              Back to portfolio
              <ArrowRight size={16} />
            </Link>
          </div>

          {notice && (
            <div
              className={`mb-5 rounded-2xl border px-4 py-3 text-sm font-semibold ${
                notice.type === "error"
                  ? "border-red-300/20 bg-red-500/15 text-red-100"
                  : "border-emerald-300/20 bg-emerald-500/15 text-emerald-100"
              }`}
            >
              {notice.message}
            </div>
          )}

          {loading ? (
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/55">
              <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />
              Loading Creator Tools...
            </div>
          ) : (
            <div className="grid gap-5">
              <InstructionTemplates
                allowed={entitlements.reusableEditingInstructions}
                templates={templates}
                form={templateForm}
                setForm={setTemplateForm}
                editingId={templateEditingId}
                onEdit={handleTemplateEdit}
                onCancel={() => {
                  setTemplateEditingId("");
                  setTemplateForm(EMPTY_TEMPLATE_FORM);
                }}
                onDelete={handleTemplateDelete}
                onSubmit={handleTemplateSubmit}
                saving={templateSaving}
              />

              <ProjectOrganization
                allowed={entitlements.projectOrganization}
                projects={projects}
                form={projectForm}
                setForm={setProjectForm}
                editingId={projectEditingId}
                onEdit={handleProjectEdit}
                onCancel={() => {
                  setProjectEditingId("");
                  setProjectForm(EMPTY_PROJECT_FORM);
                }}
                onDelete={handleProjectDelete}
                onSubmit={handleProjectSubmit}
                saving={projectSaving}
              />

              <BatchPlanning
                allowed={entitlements.batchProjectPlanning}
                form={batchForm}
                setForm={setBatchForm}
                onSubmit={handleBatchSubmit}
                saving={batchSaving}
              />

              <WorkflowBoard
                allowed={entitlements.contentWorkflowBoard}
                projects={projects}
              />

              <StudioReviewCta allowed={entitlements.discountedCreatorReview} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
